import {
    Component,
    Input,
    Output,
    EventEmitter,
    ChangeDetectorRef,
    OnInit,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { startWith, auditTime, map, distinctUntilChanged, tap } from 'rxjs/operators';

export class QueueOptionSets {
    key!: string;
    label!: string;
    source?: string;
    options?: string[];
}

@Component({
    selector: 'cv-json-filter-builder',
    templateUrl: './json-filter-builder.component.html',
    styleUrls: ['./json-filter-builder.component.scss'],
})
export class QueueRuleBuilderComponent implements OnInit, OnChanges {
    @Input() optionSets: QueueOptionSets[] = [
        { key: 'total', label: 'Order Total' },
        {
            key: 'Status',
            label: 'Status',
            options: [
                'Queued',
                'In Quality',
                'Approved',
                'Revision Received',
                'Returned for Revision',
            ],
        },
        { key: 'discount', label: 'Discount' },
        { key: 'shippingCountry', label: 'Shipping Country', options: ['US', 'MX', 'CA'] },
        { key: 'customer.type', label: 'Customer Type', options: ['regular', 'premium', 'vip'] },
        { key: 'customer.name', label: 'Customer Name' },
        { key: 'orderId', label: 'Order ID' },
    ];

    @Input() existingLogic: any | null = null;
    @Output() logicChange = new EventEmitter<string>(); // serialized JsonLogic

    readonly operatorMap: Record<string, string> = {
        '==': 'Equals',
        '!=': 'Not equals',
        '>': 'Greater than',
        '>=': 'Greater or equal',
        '<': 'Less than',
        '<=': 'Less or equal',
        in: 'Contains',
    };

    operatorOptions: Array<{ label: string; value: string }> = [];
    fieldOptions: Array<{ label: string; value: string }> = [];
    logicOpOptions = [
        { label: 'AND (all true)', value: 'and' },
        { label: 'OR (at least one)', value: 'or' },
    ];

    /** Root contains singles and (flat) groups */
    form = this.fb.group({
        rules: this.fb.array([] as FormGroup[]), // root singles
        groups: this.fb.array([] as FormGroup[]), // root groups
    });

    constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef) {
        // seed with one single so the canvas isn’t empty

        this.form.valueChanges
            .pipe(
                startWith(this.form.value),
                auditTime(0),
                map(() => this.safeStringify(this.buildRootJson())),
                distinctUntilChanged(),
            )
            .subscribe((json) => {
                this.logicChange.emit(json);
            });
    }

    ngOnInit(): void {
        this.operatorOptions = Object.entries(this.operatorMap).map(([value, label]) => ({
            label,
            value,
        }));
        this.fieldOptions = this.optionSets.map((op) => ({ label: op.label, value: op.key }));
    }

    // ---------- accessors
    rulesArray(g: FormGroup): FormArray<FormGroup> {
        return g.get('rules') as FormArray<FormGroup>;
    }
    groupsArray(g: FormGroup): FormArray<FormGroup> {
        return g.get('groups') as FormArray<FormGroup>;
    }

    // ---------- factories
    createRule(): FormGroup {
        const fg = this.fb.group({
            field: new FormControl<string>('', { nonNullable: true }),
            operator: new FormControl<string>('==', { nonNullable: true }),
            value: new FormControl<any>(''),
        });
        fg.get('field')!.valueChanges.subscribe(() => this.onFieldOrOperatorChanged(fg));
        fg.get('operator')!.valueChanges.subscribe(() => this.onFieldOrOperatorChanged(fg));
        return fg;
    }

    /** Group can be AND or OR and contains ONLY rules (no nested groups). */
    createGroup(op: 'and' | 'or' = 'and'): FormGroup {
        return this.fb.group({
            op: new FormControl<'and' | 'or'>(op, { nonNullable: true }),
            rules: this.fb.array([] as FormGroup[]),
            groups: this.fb.array([] as FormGroup[]), // kept for compatibility; never used for nesting
        });
    }

    // ---------- add/remove (nesting disallowed)
    addRule(container: FormGroup): void {
        this.rulesArray(container).push(this.createRule());
        this.emitLogic();
    }
    removeRule(container: FormGroup, idx: number): void {
        this.rulesArray(container).removeAt(idx);
        this.emitLogic();
    }

    addGroup(container: FormGroup, op: 'and' | 'or' = 'and'): void {
        if (container !== this.form) {
            return;
        }
        const g = this.createGroup(op);
        this.addRule(g); // seed with one rule
        this.groupsArray(container).push(g);
        this.emitLogic();
    }
    removeGroup(container: FormGroup, idx: number): void {
        this.groupsArray(container).removeAt(idx);
        this.emitLogic();
    }

    // ---------- UI helpers
    onFieldOrOperatorChanged(rule: FormGroup): void {
        rule.get('value')!.setValue('');
        this.cdr.detectChanges();
    }
    areConditionsEmpty(): boolean {
        return this.rulesArray(this.form).length === 0 && this.groupsArray(this.form).length === 0;
    }

    // ---------- emit
    private emitLogic(): void {
        const json = this.safeStringify(this.buildRootJson());
        this.logicChange.emit(json);
    }
    private safeStringify(node: any | null): string {
        return node ? JSON.stringify(node) : '';
    }

    // ---------- build JsonLogic (root AND is virtual)
    private buildRootJson(): any | null {
        const items: any[] = [];

        // singles at root
        for (const r of this.rulesArray(this.form).controls) {
            const node = this.buildJsonFromRule(r);
            if (node) items.push(node);
        }
        // groups at root (keep their op; groups contain only rules)
        for (const g of this.groupsArray(this.form).controls) {
            const node = this.buildJsonFromGroup(g);
            if (node) items.push(node);
        }

        if (items.length === 0) return null;
        if (items.length === 1) return items[0]; // single item (rule or group)
        return { and: items }; // virtual root AND for 2+ items
    }

    private buildJsonFromGroup(group: FormGroup): any | null {
        const op = group.get('op')!.value as 'and' | 'or';
        const children: any[] = [];

        for (const r of this.rulesArray(group).controls) {
            const node = this.buildJsonFromRule(r);
            if (node) children.push(node);
        }

        if (children.length === 0) return null;
        // preserve wrapper even for 1 child so UI reload shows the same group
        return { [op]: children };
    }

    private buildJsonFromRule(rule: FormGroup): any | null {
        const fieldRaw = rule.get('field')!.value;
        const field = this.canonicalKey(fieldRaw);
        const operator = rule.get('operator')!.value;
        const rawValue = rule.get('value')!.value;

        if (!field || !operator || rawValue === '' || rawValue == null) return null;

        const vref = { var: field };
        if (operator === 'in') return { in: [String(rawValue), vref] };

        const value = this.normalizeValue(rawValue);
        return { [operator]: [vref, value] };
    }

    // ---------- load / parse (no nested groups)
    ngOnChanges(changes: SimpleChanges): void {
        if ('existingLogic' in changes) this.loadFromLogic(this.existingLogic);
    }

    private loadFromLogic(logic: any | null): void {
        this.rulesArray(this.form).clear();
        this.groupsArray(this.form).clear();

        if (!logic) {
            this.addRule(this.form);
            this.cdr.detectChanges();
            this.emitLogic();
            return;
        }

        let parsed = logic;
        if (typeof logic === 'string') {
            try {
                parsed = JSON.parse(logic);
            } catch {
                parsed = null;
            }
        }
        if (!parsed || typeof parsed !== 'object') {
            this.addRule(this.form);
            this.cdr.detectChanges();
            this.emitLogic();
            return;
        }

        const pushRootRule = (node: any) => {
            const r = this.createRule();
            if (this.fillRuleFromLogic(node, r)) this.rulesArray(this.form).push(r);
        };
        const pushRootGroup = (op: 'and' | 'or', arr: any[]) => {
            const g = this.createGroup(op);
            this.groupsArray(this.form).push(g);
            for (const child of arr) {
                // groups-in-groups are flattened to rules (policy: no nesting)
                const ck = child && typeof child === 'object' ? Object.keys(child)[0] : null;
                if ((ck === 'and' || ck === 'or') && Array.isArray(child[ck])) {
                    for (const grand of child[ck]) pushRuleIntoGroup(g, grand);
                } else {
                    pushRuleIntoGroup(g, child);
                }
            }
        };
        const pushRuleIntoGroup = (group: FormGroup, node: any) => {
            const r = this.createRule();
            if (this.fillRuleFromLogic(node, r)) this.rulesArray(group).push(r);
        };

        const rootKey = Object.keys(parsed)[0];
        if ((rootKey === 'and' || rootKey === 'or') && Array.isArray(parsed[rootKey])) {
            // If root is AND, render children as top-level items (singles/groups).
            // If root is OR, create a single OR group at root with its rules.
            if (rootKey === 'and') {
                for (const child of parsed.and) {
                    const ck = child && typeof child === 'object' ? Object.keys(child)[0] : null;
                    if (ck === 'and' || ck === 'or') {
                        pushRootGroup(ck as 'and' | 'or', child[ck]);
                    } else {
                        pushRootRule(child);
                    }
                }
            } else {
                // root OR
                pushRootGroup('or', parsed.or);
            }
        } else {
            // single rule at root
            pushRootRule(parsed);
        }

        if (this.areConditionsEmpty()) this.addRule(this.form);
        this.cdr.detectChanges();
        this.emitLogic();
    }

    // ---------- parsing helpers
    private fillRuleFromLogic(entry: any, rule: FormGroup): boolean {
        if (!entry || typeof entry !== 'object') return false;
        const op = Object.keys(entry)[0];
        const values = entry[op];

        if (op === 'in' && Array.isArray(values) && values.length >= 2 && values[1]?.var) {
            const field = this.canonicalKey(values[1].var) ?? values[1].var;
            const first = Array.isArray(values[0]) ? values[0][0] : values[0];
            rule.patchValue({ field, operator: 'in' });
            rule.get('value')!.setValue(this.coerceScalar(first));
            return true;
        }

        if (
            ['==', '===', '!=', '!==', '>', '>=', '<', '<='].includes(op) &&
            Array.isArray(values) &&
            values.length >= 2
        ) {
            const [a, b] = values;
            const isVarA = a && typeof a === 'object' && 'var' in a;
            const isVarB = b && typeof b === 'object' && 'var' in b;
            if (!isVarA && !isVarB) return false;

            const field = this.canonicalKey(isVarA ? a.var : b.var) ?? (isVarA ? a.var : b.var);
            const value = isVarA ? b : a;

            rule.patchValue({ field, operator: op === '===' ? '==' : op === '!==' ? '!=' : op });
            rule.get('value')!.setValue(this.coerceScalar(value));
            return true;
        }
        return false;
    }

    private canonicalKey(raw: string | null | undefined): string | null {
        if (!raw) return null;
        const lc = String(raw).toLowerCase();
        const found = this.optionSets.find((o) => o.key.toLowerCase() === lc);
        return found ? found.key : null;
    }
    shouldUseDropdown(rule: FormGroup): boolean {
        const field = this.canonicalKey(rule.get('field')!.value);
        const operator = rule.get('operator')!.value;
        return (
            this.hasOptionsFor(field) &&
            (operator === '==' || operator === '!=' || operator === 'in')
        );
    }
    hasOptionsFor(fieldKey: string | null): boolean {
        const meta = this.optionSets.find((o) => o.key === (this.canonicalKey(fieldKey) ?? ''));
        return !!(meta && meta.options && meta.options.length);
    }
    private normalizeValue(v: any): any {
        if (typeof v === 'string' && v.trim() !== '' && !isNaN(+v)) return +v;
        return v;
    }
    private coerceScalar(v: any): any {
        if (typeof v === 'number') return v;
        if (typeof v === 'string' && v.trim() !== '' && !isNaN(+v)) return +v;
        return typeof v === 'string' ? v : String(v ?? '');
    }
    // ---------- clear all ----------
    clearAll(): void {
        this.rulesArray(this.form).clear();
        this.groupsArray(this.form).clear();

        // Trigger UI and logic refresh
        this.cdr.detectChanges();
        this.emitLogic();
    }
    // ---------- field dropdown options ----------
    getFieldOptionStrings(fieldKey: string | null): string[] {
        const key = this.canonicalKey(fieldKey);
        if (!key) return [];
        const meta = this.optionSets.find((o) => o.key === key);
        return meta?.options ?? [];
    }
}
